import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import { DollarSign, ShoppingBag, Users, Package, AlertTriangle, TrendingUp } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="ipv-eyebrow">Loading…</div>;

  const tiles = [
    { icon: DollarSign, label: "Total revenue", value: fmtMoney(stats.total_revenue), accent: "text-[#0A1C40]" },
    { icon: ShoppingBag, label: "Total orders", value: stats.total_orders },
    { icon: TrendingUp, label: "Last 7 days", value: fmtMoney(stats.week_revenue), sub: `${stats.week_orders} orders`, accent: "text-green-700" },
    { icon: Package, label: "Active products", value: stats.total_products },
    { icon: Users, label: "Customers", value: stats.total_customers },
    { icon: AlertTriangle, label: "Low stock items", value: stats.low_stock.length, accent: stats.low_stock.length > 0 ? "text-[#DC2626]" : "" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="ipv-eyebrow text-[#0A1C40]">Dashboard</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mt-2">Store overview</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
        {tiles.map((t) => (
          <div key={t.label} className="bg-white border border-gray-200 p-5" data-testid={`stat-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <t.icon className={`w-4 h-4 ${t.accent || "text-gray-500"}`} />
            <div className={`font-display font-black text-3xl mt-3 ${t.accent || ""}`}>{t.value}</div>
            <div className="ipv-eyebrow text-gray-500 mt-1">{t.label}</div>
            {t.sub && <div className="text-xs text-gray-500 mt-1">{t.sub}</div>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <section className="bg-white border border-gray-200 p-6">
          <div className="ipv-eyebrow mb-4">Top products (revenue)</div>
          {stats.top_products.length === 0 ? (
            <p className="text-sm text-gray-500">No sales yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 ipv-eyebrow">
                  <th className="pb-3">Product</th>
                  <th className="pb-3 text-right">Qty</th>
                  <th className="pb-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.top_products.map((p) => (
                  <tr key={p.product_id}>
                    <td className="py-2.5 truncate max-w-[180px]">{p.name}</td>
                    <td className="py-2.5 text-right">{p.qty}</td>
                    <td className="py-2.5 text-right font-bold">{fmtMoney(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="bg-white border border-gray-200 p-6">
          <div className="ipv-eyebrow mb-4 flex items-center justify-between">
            <span>Low stock alerts</span>
            <Link to="/admin/products" className="text-xs text-[#0A1C40] hover:underline normal-case tracking-normal">Manage →</Link>
          </div>
          {stats.low_stock.length === 0 ? (
            <p className="text-sm text-gray-500">All products are well stocked.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {stats.low_stock.slice(0, 8).map((p) => (
                <div key={p.id} className="py-2.5 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-bold">{p.name}</div>
                    <div className="text-xs text-gray-500">SKU {p.sku || "—"}</div>
                  </div>
                  <div className={`font-display font-bold ${p.stock === 0 ? "text-[#DC2626]" : "text-yellow-700"}`}>
                    {p.stock} left
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {stats.daily_revenue.length > 0 && (
        <section className="bg-white border border-gray-200 p-6 mt-6">
          <div className="ipv-eyebrow mb-4">Last 30 days revenue</div>
          <div className="flex items-end gap-1 h-40">
            {(() => {
              const max = Math.max(...stats.daily_revenue.map(d => d.revenue), 1);
              return stats.daily_revenue.map((d) => (
                <div key={d.date} className="flex-1 group relative">
                  <div
                    className="bg-[#0A1C40] hover:bg-[#DC2626] transition-colors"
                    style={{ height: `${(d.revenue / max) * 100}%`, minHeight: d.revenue > 0 ? "2px" : "0" }}
                    title={`${d.date}: ${fmtMoney(d.revenue)}`}
                  />
                </div>
              ));
            })()}
          </div>
        </section>
      )}
    </div>
  );
}
